import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useWorldStore } from '../state/worldStore';
import { VIDEO_OFF } from '../config';

// A video "cameo" panel inside a splat room — the avatar in the room with you.
// Plays the greeting once (with audio — entering the room was a user click, so
// autoplay is allowed; falls back to muted if the browser refuses), then
// switches to the silent working loop. The video's own background is a
// perspective crop of the SAME pano the splat was built from, so the panel
// reads as part of the room; a soft alpha feather hides the rectangle edges.

// Rounded-corner feather mask so the plane has no hard edges against the splat.
function makeFeatherAlpha() {
  const S = 256;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, S, S);
  const m = 22; // feather margin
  const grad = (x0, y0, x1, y1) => {
    const g = ctx.createLinearGradient(x0, y0, x1, y1);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(1, 'rgba(255,255,255,1)');
    return g;
  };
  ctx.fillStyle = '#fff';
  ctx.fillRect(m, m, S - m * 2, S - m * 2);
  ctx.fillStyle = grad(0, 0, m, 0); ctx.fillRect(0, m, m, S - m * 2);
  ctx.fillStyle = grad(S, 0, S - m, 0); ctx.fillRect(S - m, m, m, S - m * 2);
  ctx.fillStyle = grad(0, 0, 0, m); ctx.fillRect(m, 0, S - m * 2, m);
  ctx.fillStyle = grad(0, S, 0, S - m); ctx.fillRect(m, S - m, S - m * 2, m);
  const t = new THREE.CanvasTexture(c);
  return t;
}

export default function VideoPanel({ greetingUrl, loopUrl, position = [0, 0.95, 0], rotationY = 0, height = 1.9, billboard = true, storageKey = null }) {
  const [texture, setTexture] = useState(null);
  const [aspect, setAspect] = useState(9 / 16);
  const videoRef = useRef(null);
  const alphaMap = useMemo(makeFeatherAlpha, []);

  // LIVE takeover — when a live Anam call is running (LiveChatDock), the card
  // swaps its recorded cameo for the WebRTC stream. Same mesh, same placement,
  // same feather: the avatar behind the glass simply comes alive.
  const liveEl = useWorldStore((s) => s.liveVideoEl);
  const [liveTexture, setLiveTexture] = useState(null);
  useEffect(() => {
    if (!liveEl) return undefined;
    videoRef.current?.pause(); // rest the recorded loop while live
    const tex = new THREE.VideoTexture(liveEl);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    setLiveTexture(tex);
    return () => {
      tex.dispose();
      setLiveTexture(null);
      // Call over — the recorded working loop resumes on the card.
      const v = videoRef.current;
      if (v) { v.loop = true; v.muted = true; v.play().catch(() => {}); }
    };
  }, [liveEl]);

  // Live placement editor (?panel=edit): arrows move the panel RELATIVE TO
  // YOUR VIEW (left/right = screen-left/right, up/down = away/toward you),
  // u/j raise/lower, [ ] rotate, - / = resize, b freezes billboarding while
  // placing. Step scales with camera distance so movement covers similar
  // screen distance wherever you stand. Each change logs a paste-ready
  // videoPanel line for rooms.js.
  const editMode = useMemo(() => new URLSearchParams(window.location.search).get('panel') === 'edit', []);
  // Tuned placement survives reloads: edit-mode changes are auto-saved to
  // localStorage (per room) and win over the rooms.js defaults until 'r'
  // resets them. Prevents losing a placement session by reloading.
  const loadSaved = () => {
    if (!storageKey) return null;
    try { return JSON.parse(localStorage.getItem(storageKey) || 'null'); } catch { return null; }
  };
  const [xf, setXf] = useState(() => loadSaved() || { pos: position, rotY: rotationY, height, billboard });
  const cameraRef = useRef(null);
  useEffect(() => {
    const saved = loadSaved();
    console.log(saved
      ? `[panel] restored saved placement (${storageKey}): ${JSON.stringify(saved)}`
      : `[panel] no saved placement for ${storageKey || '(no key)'} — using rooms.js defaults`);
    setXf(saved || { pos: position, rotY: rotationY, height, billboard });
  }, [position, rotationY, height, billboard, storageKey]);
  useEffect(() => {
    if (!editMode) return;
    const onKey = (e) => {
      // NEVER capture typing. This listener is on window, so without this a
      // login form (or any text field) on the page loses every u/j/-/= etc.
      // to the panel editor — the characters just vanish from the input.
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      // 'b' toggles billboarding AS A SAVED SETTING, not a placement-time
      // freeze: a panel placed as a screen (the meet panel on a wall) must
      // hold its angle like a screen; billboarding is for free-standing
      // composited characters at positions in the room. The toggle rides the
      // same persistence as position so the choice survives and pastes out.
      if (e.key === 'b') {
        setXf((s) => {
          const next = { ...s, billboard: !(s.billboard ?? billboard) };
          if (storageKey) localStorage.setItem(storageKey, JSON.stringify(next));
          console.log(`[panel] billboard ${next.billboard ? 'ON (faces the viewer)' : 'OFF (fixed angle — rotate with [ ])'}`);
          return next;
        });
        return;
      }
      if (e.key === 'r' && storageKey) {
        localStorage.removeItem(storageKey);
        setXf({ pos: position, rotY: rotationY, height, billboard });
        console.log('[panel] reset to rooms.js defaults');
        return;
      }
      setXf((s) => {
        const [x, y, z] = s.pos;
        // Camera-relative ground axes: fwd = view direction flattened onto
        // the floor, right = perpendicular. Falls back to world axes if the
        // camera isn't known yet.
        let fx = 0, fz = -1, rx = 1, rz = 0;
        const cam = cameraRef.current;
        if (cam) {
          const dir = new THREE.Vector3();
          cam.getWorldDirection(dir);
          dir.y = 0;
          if (dir.lengthSq() > 1e-6) {
            dir.normalize();
            fx = dir.x; fz = dir.z;
            rx = -dir.z; rz = dir.x; // fwd × up → screen-right on the ground
          }
        }
        // Step ~proportional to how far you are (same on-screen travel near or far)
        const dist = cam ? Math.max(1, cam.position.distanceTo(new THREE.Vector3(x, y, z))) : 3;
        const step = (e.shiftKey ? 0.008 : 0.04) * dist;
        let next = s;
        if (e.key === 'ArrowLeft') next = { ...s, pos: [x - rx * step, y, z - rz * step] };
        else if (e.key === 'ArrowRight') next = { ...s, pos: [x + rx * step, y, z + rz * step] };
        else if (e.key === 'ArrowUp') next = { ...s, pos: [x + fx * step, y, z + fz * step] };
        else if (e.key === 'ArrowDown') next = { ...s, pos: [x - fx * step, y, z - fz * step] };
        else if (e.key === 'PageUp' || e.key === 'u') next = { ...s, pos: [x, y + step * 0.6, z] };
        else if (e.key === 'PageDown' || e.key === 'j') next = { ...s, pos: [x, y - step * 0.6, z] };
        else if (e.key === '[') next = { ...s, rotY: s.rotY + 0.08 };
        else if (e.key === ']') next = { ...s, rotY: s.rotY - 0.08 };
        else if (e.key === '-') next = { ...s, height: Math.max(0.5, s.height - 0.06) };
        else if (e.key === '=') next = { ...s, height: s.height + 0.06 };
        else return s;
        e.preventDefault();
        const r = (v) => Math.round(v * 100) / 100;
        if (storageKey) localStorage.setItem(storageKey, JSON.stringify(next));
        console.log(`videoPanel: { pos: [${next.pos.map(r).join(', ')}], rotY: ${r(next.rotY)}, height: ${r(next.height)}, billboard: ${next.billboard ?? billboard} }`);
        return next;
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editMode]);

  useEffect(() => {
    // Stills test (config.VIDEO_OFF): the recorded ambient loop stays off.
    // The LIVE takeover above is untouched — a call someone started is a
    // feature, not ambience.
    const firstUrl = VIDEO_OFF ? null : (greetingUrl || loopUrl);
    if (!firstUrl) return;

    const video = document.createElement('video');
    videoRef.current = video;
    video.crossOrigin = 'anonymous';
    video.playsInline = true;
    video.preload = 'auto';

    let disposed = false;
    let tex = null;

    const startLoop = () => {
      if (disposed || !loopUrl) return;
      video.src = loopUrl;
      video.loop = true;
      video.muted = true;
      video.play().catch(() => {});
    };

    const onMeta = () => {
      if (disposed) return;
      if (video.videoWidth && video.videoHeight) setAspect(video.videoWidth / video.videoHeight);
      if (!tex) {
        tex = new THREE.VideoTexture(video);
        tex.colorSpace = THREE.SRGBColorSpace;
        setTexture(tex);
      }
    };
    video.addEventListener('loadedmetadata', onMeta);

    if (greetingUrl) {
      video.src = greetingUrl;
      video.loop = false;
      video.muted = false;
      video.addEventListener('ended', startLoop);
      // Entering the room was a click (a user gesture), so audio usually
      // passes — but if the browser refuses, greet silently rather than never.
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    } else {
      startLoop();
    }

    // A missing/broken file = no panel, never a black rectangle in the room.
    const onError = () => { if (!disposed) setTexture(null); };
    video.addEventListener('error', onError);

    return () => {
      disposed = true;
      video.pause();
      video.removeAttribute('src');
      video.load();
      tex?.dispose();
    };
  }, [greetingUrl, loopUrl]);

  // Billboard: the panel yaw-tracks the camera every frame, so the flat plane
  // holds its perspective as the user moves (the user's movement delta applied
  // back to the panel). Yaw-only — the panel stays upright, feet planted.
  // The configured rotY becomes an offset for art-directed skew if wanted.
  const meshRef = useRef();
  useFrame(({ camera }) => {
    cameraRef.current = camera;
    // Center-crop the live stream into the card's aspect (the stream may be
    // landscape; the card is the booth-window portrait) — no reflow, the face
    // stays centered, the frame keeps matting the edges.
    if (liveTexture && liveEl?.videoWidth && liveEl?.videoHeight) {
      const va = liveEl.videoWidth / liveEl.videoHeight;
      if (va > aspect) {
        const rx = aspect / va;
        liveTexture.repeat.set(rx, 1);
        liveTexture.offset.set((1 - rx) / 2, 0);
      } else {
        const ry = va / aspect;
        liveTexture.repeat.set(1, ry);
        liveTexture.offset.set(0, (1 - ry) / 2);
      }
    }
    if (!meshRef.current) return;
    if (xf.billboard ?? billboard) {
      const [px, , pz] = xf.pos;
      meshRef.current.rotation.y = Math.atan2(camera.position.x - px, camera.position.z - pz) + (editMode ? xf.rotY - rotationY : 0);
    } else {
      meshRef.current.rotation.y = xf.rotY;
    }
  });

  // In edit mode a panel with nothing to show still renders — as a placement
  // GHOST — because a meet panel has no recorded cameo and only comes alive
  // during a live call, and placing an invisible thing is impossible.
  if (!texture && !liveTexture && !editMode) return null;
  const width = xf.height * aspect;
  const ghost = !liveTexture && !texture;

  return (
    <mesh ref={meshRef} position={xf.pos} rotation={[0, xf.rotY, 0]}>
      <planeGeometry args={[width, xf.height]} />
      <meshBasicMaterial
        map={liveTexture || texture}
        color={ghost ? '#ffb25e' : '#ffffff'}
        opacity={ghost ? 0.55 : 1}
        alphaMap={alphaMap}
        transparent
        toneMapped={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}
